import type { RoutingPolicyMode } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import {
  POLICY_MODE_OPTIONS,
  policyModeHintKey,
  policyModeLabelKey,
} from '../../pages/tenantRoutingShared.ts';
import { Button, InfoHint, Select } from '../../ui/index.ts';
import type { SupplyPanelWrites } from './useSupplyPanelWrites.ts';

/**
 * Execution policy, at the foot of the supply panel (#1428).
 *
 * It stays in this panel rather than becoming its own section because it is a
 * property of the same decision the table above makes — how hard to try the
 * supply listed there — but it sits below the rows and not in a row, because
 * it is SKU-level: it applies to every target at once.
 */
export function SupplyPolicyFooter({
  policy,
}: {
  readonly policy: SupplyPanelWrites['policy'];
}): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-line-soft/50 px-4 py-2.5">
      <span className="inline-flex items-center gap-1 text-label text-paper-low">
        {t('tenant_routing.default_channels.policy_mode')}
        <InfoHint
          label={t('sku_detail.hint.label')}
          content={t('sku_detail.hint.policy_mode')}
          testId="sku-supply-hint-policy-mode"
        />
      </span>
      <Select
        className="min-w-52"
        value={policy.draft}
        onValueChange={(value) => {
          const mode: RoutingPolicyMode | undefined = POLICY_MODE_OPTIONS.find(
            (candidate) => candidate === value,
          );
          if (mode !== undefined) {
            policy.setDraft(mode);
          }
        }}
        options={POLICY_MODE_OPTIONS.map((mode) => ({
          value: mode,
          label: t(policyModeLabelKey(mode)),
        }))}
        testId="sku-policy-mode"
      />
      <span className="min-w-0 flex-1 text-label text-paper-low">
        {t(policyModeHintKey(policy.draft))}
      </span>
      <Button
        size="sm"
        disabled={policy.saveDisabled}
        onClick={policy.save}
        data-testid="sku-policy-save"
      >
        {t('tenant_routing.default_channels.save_policy')}
      </Button>
    </div>
  );
}
